import React, { useState, useEffect } from 'react';
import { Copy, Check, ArrowLeft } from 'lucide-react';

const CopyPromptPage = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestPrompt = async () => {
      try {
        const token = localStorage.getItem('admin_access_token');
        if (!token) {
          window.location.href = '/admin/login?redirect=/admin/copy-prompt';
          return;
        }

        const response = await fetch('/api/analytics/admin/intelligence?days=7', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch');

        const data = await response.json();
        const recommendations = data.recommendations || [];
        
        const promptText = generatePrompt(recommendations, data.metrics);
        setPrompt(promptText);
        setLoading(false);
        
        setTimeout(() => {
          const textArea = document.getElementById('prompt-text');
          if (textArea) {
            textArea.select();
            textArea.setSelectionRange(0, 99999);
          }
        }, 100);
        
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchLatestPrompt();
  }, []);

  const generatePrompt = (recommendations, metrics) => {
    if (!recommendations || recommendations.length === 0) {
      return "No critical improvements needed this week. Keep up the good work!";
    }

    let lines = [
      "IMPORTANT: Before implementing each change below, use web_search_tool to find the LATEST best practices and strategies (2024-2025) for that specific improvement. Apply current industry knowledge, not outdated tactics.",
      "",
      "Implement these improvements for MCM based on this week's analytics:",
      ""
    ];

    recommendations.forEach((rec, i) => {
      lines.push(`## ${i + 1}. ${rec.title}`);
      lines.push(`Priority: ${rec.priority?.toUpperCase()}`);
      lines.push(`Problem: ${rec.problem}`);
      lines.push(`Impact: ${rec.impact}`);
      lines.push("Changes needed:");
      (rec.actions || []).slice(0, 3).forEach(action => {
        lines.push(`- ${action}`);
      });
      lines.push(`Research: Search for latest strategies on '${rec.title?.toLowerCase()}' before implementing`);
      lines.push("");
    });

    lines.push("---");
    lines.push("EXECUTION RULES:");
    lines.push("1. Research each item using web search for current best practices");
    lines.push("2. Apply the latest strategies, not generic solutions");
    lines.push("3. Implement changes");
    lines.push("4. Test thoroughly");
    lines.push("5. Confirm completion with results");
    lines.push("");
    
    if (metrics) {
      lines.push(`Current metrics: ${metrics.traffic?.unique_visitors || 0} visitors, ${metrics.reviewer_funnel?.signup_completes || 0} signups, $${(metrics.revenue?.gross || 0).toFixed(2)} revenue`);
    }

    return lines.join('\n');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      // Fallback
      const textArea = document.getElementById('prompt-text');
      if (textArea) {
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#F2D857] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading prompt...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.href = '/admin/login'}
            className="bg-[#F2D857] text-[#111827] px-6 py-2 rounded-lg font-medium"
          >
            Login to Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111827] p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-[#F2D857] font-semibold">MCM Weekly Prompt</h1>
        </div>

        {/* Big Copy Button */}
        <button
          onClick={copyToClipboard}
          className={`w-full flex items-center justify-center gap-3 p-4 rounded-xl font-semibold text-lg mb-6 transition-all ${
            copied 
              ? 'bg-green-500 text-white' 
              : 'bg-[#F2D857] text-[#111827] hover:bg-[#D4B93C]'
          }`}
        >
          {copied ? (
            <>
              <Check className="w-6 h-6" />
              COPIED! Now paste into Emergent
            </>
          ) : (
            <>
              <Copy className="w-6 h-6" />
              TAP TO COPY PROMPT
            </>
          )}
        </button>

        {/* Prompt Text Area */}
        <div className="bg-[#1F2937] rounded-xl border border-[#374151] overflow-hidden">
          <div className="bg-[#374151] px-4 py-2 flex items-center justify-between">
            <span className="text-[#F2D857] text-sm font-medium">EMERGENT PROMPT</span>
            <span className="text-gray-500 text-xs">Tap the box to select all</span>
          </div>
          <textarea
            id="prompt-text"
            readOnly
            value={prompt}
            onClick={(e) => e.target.select()}
            className="w-full bg-transparent text-gray-200 p-4 text-sm font-mono resize-none focus:outline-none"
            style={{ minHeight: '400px' }}
          />
        </div>

        {/* Instructions */}
        <p className="text-center text-gray-500 text-sm mt-4">
          Tap the yellow button above, then paste into Emergent
        </p>
      </div>
    </div>
  );
};

export default CopyPromptPage;
